import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo } from '../data/siteData';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  // Prevent scroll when fullscreen menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const isActive = (path) => location.pathname === path;
  const isDropdownActive = (items) => items?.some(item => location.pathname === item.href);

  const mainNavLinks = [
    { label: "Forside", href: "/" },
    { label: "Vi køber guld & sølv", href: "/vi-koeber-guld-og-soelv", isSpecial: true },
    {
      label: "Vores Værksted",
      href: "/guldsmedevaerksted",
      dropdown: [
        { label: "Guldsmedeværksted", href: "/guldsmedevaerksted" },
        { label: "Smykker efter eget design", href: "/smykker-eget-design" },
        { label: "Urereparation", href: "/urereparation" },
      ]
    },
    { label: "Brands", href: "/brands" },
    { label: "Om os", href: "/om-os" },
    { label: "Inspiration", href: "/inspiration" },
    { label: "Kontakt", href: "/kontakt" },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 w-full max-w-[1440px] mx-auto px-3 sm:px-6 lg:px-8 pt-3 sm:pt-4 lg:pt-5 pb-2 transition-all duration-300 pointer-events-none">
        {/* Floating Frosted Glass Container */}
        <div className="pointer-events-auto relative backdrop-blur-xl bg-white border border-stone-200/80 shadow-[0_12px_40px_-8px_rgba(11,19,43,0.12),0_1px_3px_rgba(0,0,0,0.04)] rounded-[16px] lg:rounded-[20px] ring-1 ring-[#0B132B]/5 px-4 sm:px-6 lg:px-9 py-3 sm:py-3.5 lg:py-4 transition-all duration-300">
          
          {/* Subtle inner highlight layer for depth */}
          <div className="absolute inset-0 rounded-[16px] lg:rounded-[20px] pointer-events-none ring-1 ring-inset ring-stone-100 shadow-[inset_0_1px_1px_rgba(255,255,255,0.9)]" />

          <div className="relative z-10 flex items-center justify-between gap-4 lg:gap-6">
            
            {/* ZONE 1: Left - Refined Logo Lockup */}
            <Link to="/" className="flex items-center shrink-0 group py-0.5">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=90,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/logo-trans.png"
                alt="Brønshøj Ure & Guld"
                className="h-11 sm:h-13 lg:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* ZONE 2: Center - Horizontal Navigation Menu (Desktop) */}
            <nav className="hidden min-[1200px]:flex items-center justify-center gap-4 xl:gap-6 flex-1 px-2">
              {mainNavLinks.map((link) => {
                if (link.dropdown) {
                  const active = isDropdownActive(link.dropdown);
                  return (
                    <div
                      key={link.label}
                      className="relative py-1 group"
                      onMouseEnter={() => setDropdownOpen(true)}
                      onMouseLeave={() => setDropdownOpen(false)}
                    >
                      <button
                        className={`text-[11px] xl:text-xs font-semibold tracking-[0.16em] uppercase transition-colors flex items-center gap-1.5 cursor-pointer py-1 relative ${
                          active
                            ? 'text-[#8C7A5B]'
                            : 'text-[#0B132B] hover:text-[#8C7A5B]'
                        }`}
                      >
                        <span>{link.label}</span>
                        <i className={`fa-solid fa-chevron-down text-[8px] transition-transform duration-200 ${dropdownOpen ? 'rotate-180 text-[#8C7A5B]' : 'opacity-60'}`} aria-hidden="true" />
                        <span className={`absolute bottom-0 left-0 w-full h-[1.5px] bg-[#8C7A5B] transition-transform duration-300 origin-left ${active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
                      </button>

                      {/* Dropdown Menu */}
                      {dropdownOpen && (
                        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-60 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                          <div className="backdrop-blur-2xl bg-[#0B132B]/95 text-white border border-[#B5A897]/30 shadow-2xl rounded-[14px] py-2 overflow-hidden ring-1 ring-white/10">
                            {link.dropdown.map((sub) => (
                              <Link
                                key={sub.href}
                                to={sub.href}
                                className={`block px-5 py-2.5 text-[11px] uppercase tracking-[0.16em] transition-all border-l-2 ${
                                  isActive(sub.href)
                                    ? 'bg-white/10 text-[#D4AF37] border-[#D4AF37] font-semibold'
                                    : 'text-stone-200 border-transparent hover:bg-white/5 hover:text-white hover:border-[#B5A897]'
                                }`}
                              >
                                {sub.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }

                const active = isActive(link.href);

                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`text-[11px] xl:text-xs font-semibold tracking-[0.16em] uppercase transition-colors whitespace-nowrap py-1 relative group ${
                      active
                        ? 'text-[#8C7A5B]'
                        : link.isSpecial
                          ? 'text-[#0B132B] font-bold hover:text-[#8C7A5B]'
                          : 'text-[#0B132B] hover:text-[#8C7A5B]'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      {link.isSpecial && <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />}
                      {link.label}
                    </span>
                    <span className={`absolute bottom-0 left-0 w-full h-[1.5px] bg-[#8C7A5B] transition-transform duration-300 origin-left ${active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
                  </Link>
                );
              })}
            </nav>

            {/* ZONE 3: Right - Mobile / Burger navigation toggle */}
            <div className="flex items-center gap-2 min-[1200px]:hidden">
              <Link
                to="/vi-koeber-guld-og-soelv"
                className="px-3.5 py-2 bg-[#0B132B] text-white text-[10px] font-medium tracking-[0.18em] uppercase rounded-[10px] shadow-xs"
              >
                Sælg guld
              </Link>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="relative flex flex-col justify-center items-center w-10 h-10 rounded-[10px] bg-[#0B132B]/5 hover:bg-[#0B132B]/10 border border-[#0B132B]/10 cursor-pointer transition-colors p-2"
                aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
              >
                {/* Refined 3-line animated burger icon */}
                <span
                  className={`w-5 h-[2px] bg-[#0B132B] rounded-full transition-all duration-300 origin-center ${
                    mobileMenuOpen ? 'rotate-45 translate-y-[5px]' : ''
                  }`}
                />
                <span
                  className={`w-5 h-[2px] bg-[#0B132B] rounded-full transition-all duration-300 my-1 ${
                    mobileMenuOpen ? 'opacity-0 scale-x-0' : 'opacity-100'
                  }`}
                />
                <span
                  className={`w-5 h-[2px] bg-[#0B132B] rounded-full transition-all duration-300 origin-center ${
                    mobileMenuOpen ? '-rotate-45 -translate-y-[5px]' : ''
                  }`}
                />
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* Full-Screen Responsive Overlay Navigation */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] flex flex-col justify-between p-6 sm:p-10 backdrop-blur-2xl bg-[#0B132B]/95 text-white animate-in fade-in zoom-in-95 duration-300 overflow-y-auto">
          
          {/* Top Bar with Logo & Close Button */}
          <div className="flex items-center justify-between border-b border-white/10 pb-6">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/807b5052-bea6-494e-8947-b0c29529a13c/logo-trans.png"
                alt="Brønshøj Ure & Guld"
                className="h-10 w-auto object-contain brightness-0 invert"
              />
            </Link>

            <button
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-all cursor-pointer"
              aria-label="Luk menu"
            >
              <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
            </button>
          </div>

          {/* Main Navigation Area (Large Vertically Stacked Links) */}
          <div className="my-auto py-8 space-y-4 max-w-xl mx-auto w-full text-center">
            
            {/* Prominent Banner Card */}
            <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-white/10 to-white/5 border border-white/15 text-center">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#D4AF37]">
                Vi køber guld & sølv til høje dagspriser
              </p>
            </div>

            <nav className="flex flex-col space-y-3 items-center text-center">
              {mainNavLinks.map((link) => {
                if (link.dropdown) {
                  return (
                    <div key={link.label} className="py-2 space-y-2 text-center w-full">
                      <div className="text-xs uppercase tracking-[0.25em] text-[#B5A897] font-semibold">
                        {link.label}
                      </div>
                      <div className="flex flex-col space-y-2 items-center justify-center">
                        {link.dropdown.map((sub) => (
                          <Link
                            key={sub.href}
                            to={sub.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`text-lg sm:text-xl font-serif tracking-wider transition-colors text-center ${
                              isActive(sub.href)
                                ? 'text-[#D4AF37] font-bold'
                                : 'text-stone-200 hover:text-white'
                            }`}
                          >
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-2xl sm:text-3xl font-serif tracking-wider uppercase transition-all block py-1 text-center ${
                      isActive(link.href)
                        ? 'text-[#D4AF37] font-bold scale-105'
                        : 'text-white/90 hover:text-[#D4AF37]'
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Footer Area with High-Contrast Primary CTA */}
          <div className="border-t border-white/10 pt-6 space-y-4 max-w-xl mx-auto w-full">
            <Link
              to="/vi-koeber-guld-og-soelv"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-center gap-3 w-full py-4 bg-[#D4AF37] hover:bg-[#c2a02e] text-[#0B132B] text-xs font-bold tracking-[0.2em] uppercase rounded-xl shadow-lg transition-all duration-300"
            >
              <i className="fa-solid fa-coins text-sm" aria-hidden="true" />
              <span>Få vurderet dit guld</span>
            </Link>

            <a
              href={`tel:${companyInfo.phoneRaw}`}
              className="flex items-center justify-center gap-2 w-full py-3.5 border border-white/20 text-white hover:bg-white/10 text-xs tracking-[0.2em] uppercase font-medium rounded-xl transition-all"
            >
              <i className="fa-solid fa-phone text-xs text-[#D4AF37]" aria-hidden="true" />
              <span>Ring: {companyInfo.phone}</span>
            </a>

            <div className="text-center text-[11px] tracking-wider uppercase text-stone-400">
              Frederikssundsvej 306A, 2700 Brønshøj
            </div>
          </div>

        </div>
      )}
    </>
  );
}
